import React, { useMemo, useCallback, useRef, useEffect } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import { useTranslation } from "react-i18next";

const TradesTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const autoSizeFrameRef = useRef(null);

  const {
    product,
    trades: propsTrades,
    currencySymbol,
    pricePrecision,
    volumePrecision,
    onRowClick,
  } = props;

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
      minWidth: 140,
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      autoHeaderHeight: false,
    }),
    []
  );

  // Map to view model
  const mapToViewModel = useCallback(
    (trades) => {
      if (!trades || !product) return [];

      const dateFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "numeric",
      };

      return trades.map((trade) => ({
        ...{ shortName: product.shortName },
        ...trade,
        ...{
          tradeDateFormatted: new Date(trade.tradeDate).toLocaleDateString(
            "en-US",
            dateFormatOptions
          ),
          priceFormatted:
            currencySymbol + Number(trade.price).toFixed(pricePrecision),
          volumeFormatted: Number(trade.volume).toFixed(volumePrecision),
        },
        ...trade.qualityAttributeValues.reduce((acc, attribute) => {
          acc[attribute.qualityAttributeId] = attribute.value;
          return acc;
        }, {}),
      }));
    },
    [product, currencySymbol, pricePrecision, volumePrecision]
  );

  // Memoize column definitions
  const columnDefs = useMemo(() => {
    if (!product) return [];

    return [
      { field: "shortName", headerName: t("MarketDataProduct"), flex: 1 },
      ...product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          field: qualityAttribute.qualityAttributeId,
          headerName: qualityAttribute.qualityAttributeName,
          flex: 1,
        })),
      { field: "termName", headerName: t("Term"), flex: 1 },
      { field: "priceFormatted", headerName: t("Price"), flex: 1 },
      { field: "volumeFormatted", headerName: t("Volume"), flex: 1 },
      {
        field: "tradeDateFormatted",
        headerName: t("TradeDate"),
        flex: 1,
        sort: "desc",
        comparator: (valueA, valueB) => {
          if (valueA == null && valueB == null) return 0;
          if (valueA == null) return -1;
          if (valueB == null) return 1;
          return new Date(valueA) - new Date(valueB);
        },
      },
    ];
  }, [product, t]);

  const mappedTrades = useMemo(() => {
    if (!product || !propsTrades) return [];
    return mapToViewModel(propsTrades);
  }, [product, propsTrades, mapToViewModel]);

  // Memoize grid options
  const gridOptions = useMemo(() => {
    const options = {
      pagination: true,
      paginationPageSize: 20,
      domLayout: "autoHeight",
    };

    // Set initial sort on tradeDate column
    const sortedColDefs = _.cloneDeep(columnDefs);
    const tradeDateCol = sortedColDefs.find(
      (col) => col.field === "tradeDateFormatted"
    );
    if (tradeDateCol) {
      tradeDateCol.sort = "desc";
      tradeDateCol.sortIndex = 0;
    }

    return options;
  }, [columnDefs]);

  useEffect(() => {
    return () => {
      if (autoSizeFrameRef.current) {
        cancelAnimationFrame(autoSizeFrameRef.current);
      }
    };
  }, []);

  // Handle row click
  const handleRowClick = useCallback(
    (params) => {
      if (onRowClick) {
        onRowClick(params.data);
      }
    },
    [onRowClick]
  );

  const autoSizeGridColumns = useCallback((params) => {
    if (!params?.api) return;

    if (autoSizeFrameRef.current) {
      cancelAnimationFrame(autoSizeFrameRef.current);
    }

    autoSizeFrameRef.current = requestAnimationFrame(() => {
      params.columnApi?.autoSizeAllColumns?.(false);
      params.api?.autoSizeAllColumns?.(false);
      autoSizeFrameRef.current = null;
    });
  }, []);

  if (!product) return <Loading />;

  return (
    <div className="trades-table">
      <ThemeableAgGrid
        className="table-submit-trades"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoSizeStrategy={{
          type: "fitCellContents",
          skipHeader: false,
          defaultMinWidth: 140,
        }}
        rowData={mappedTrades}
        gridOptions={gridOptions}
        onRowClicked={onRowClick ? handleRowClick : undefined}
        onFirstDataRendered={autoSizeGridColumns}
        onRowDataUpdated={autoSizeGridColumns}
        onGridSizeChanged={autoSizeGridColumns}
        getRowId={(params) => params.data.tradeId.toString()}
        animateRows={true}
        allowHorizontalScroll={true}
        domLayout="autoHeight"
      />
    </div>
  );
};

export default React.memo(TradesTable);
